import { Block } from "@business-finland/wif-ui-lib";
import dynamic from "next/dynamic";
import { useReducer } from "react";

import { localesEmployers } from "../../../constants/locale";
import useEmployerSite from "../../../contexts/EmployerSiteContext";
import { clsx } from "../../../util/clsx";
import { getCompanyFormClsx, getCompanyFormId } from "./helpers";
import { companyFormDefaultText } from "./constants";
import {
  DispatchContext,
  StateContext,
  companyFormDefaultState,
  companyFormReducer,
} from "./store";
import CompanyFormActions from "./components/Actions";
import CompanyFormProgressBar from "./components/ProgressBar";
import CompanyIdField from "./components/CompanyId";
import CompanyCriteria from "./components/Criteria";
import { CompanyName, CompanyEmail, CompanyPhone } from "./components/Contact";
import { TypeOfServices, LocalDescription } from "./components/Services";
import CompanyLogo from "./components/Logo";
import CompanyTerms from "./components/Terms";
import { CompanyFormContext } from "./contexts";
import CompanyFieldset from "./components/Fieldset";
import CompanyFormHeader from "./components/Header";
import ThankYou from "./components/ThankYou";
import useSubmit from "./useSubmit";
import type { ICompanyFormProps } from "./types";
import ErrorMessage from "./components/ErrorMessage";

const HtmlParser = dynamic(() => import("../../../components/HtmlParser"));

export default function CompanyForm(
  props: Wif.Mgnl.Node<ICompanyFormProps>
): JSX.Element {
  const {
    heading = companyFormDefaultText.heading,
    leadText = companyFormDefaultText.leadText,
    thankYouHeading = companyFormDefaultText.thankYouHeading,
    thankYouLeadText = companyFormDefaultText.thankYouLeadText,
    fieldRequiredErrorMessage = companyFormDefaultText.fieldRequiredErrorMessage,
    unmetCriteriaErrorMessage = companyFormDefaultText.unmetCriteriaErrorMessage,
    fieldInvalidFormatErrorMessage = companyFormDefaultText.fieldInvalidFormatErrorMessage,
    unsupportedFileTypeErrorMessage = companyFormDefaultText.unsupportedFileTypeErrorMessage,
    companyIdHeading = companyFormDefaultText.companyIdHeading,
    companyCriteriaApprovedHeading = companyFormDefaultText.companyCriteriaApprovedHeading,
    companyContactHeading = companyFormDefaultText.companyContactHeading,
    companyLogoHeading = companyFormDefaultText.companyLogoHeading,
    companyServicesHeading = companyFormDefaultText.companyServicesHeading,
    companyServicesLeadText = companyFormDefaultText.companyServicesLeadText,
    instructions,
    companyIdField,
    companyEmailField,
    companyNameField,
    companyPhoneField,
    companyLogoField,
    typeOfServiceField,
    companyDescriptionField,
    companyWebsiteField,
    companyTermsField = companyFormDefaultText.termsField,
    companyTermsField2,
    services = [],
    companyApprovalCriteriaList = [],
  } = props;

  const { isEmployerSite } = useEmployerSite();
  const [state, dispatch] = useReducer(
    companyFormReducer,
    companyFormDefaultState
  );
  const handleFormSubmit = useSubmit(dispatch);

  const id = getCompanyFormId();
  const className = clsx(
    getCompanyFormClsx(),
    isEmployerSite ? getCompanyFormClsx("employers") : ""
  );

  if (state.isDone) {
    return (
      <ThankYou
        id={id}
        className={className}
        heading={thankYouHeading}
        leadText={thankYouLeadText}
      />
    );
  }

  return (
    <div key={id} id={id} className={className}>
      <CompanyFormContext.Provider
        value={{ fieldRequiredErrorMessage, fieldInvalidFormatErrorMessage }}
      >
        <DispatchContext.Provider value={dispatch}>
          <StateContext.Provider value={state}>
            <CompanyFormHeader
              heading={heading}
              leadText={leadText}
              instructions={instructions}
            />
            <CompanyFormProgressBar />

            <Block
              as="form"
              id={getCompanyFormId("fields")}
              topPadding={Block.Padding.NONE}
              bottomPadding={Block.Padding.SMALL}
              onSubmit={handleFormSubmit}
            >
              <Block.Content>
                {state.currentStep === 1 ? (
                  <CompanyIdField
                    key={1}
                    heading={companyIdHeading}
                    unmetCriteriaErrorMessage={unmetCriteriaErrorMessage}
                    {...companyIdField}
                  />
                ) : state.currentStep === 2 ? (
                  <>
                    <CompanyIdField
                      key={2}
                      heading={companyIdHeading}
                      value={state.companyId}
                      readOnly
                      {...companyIdField}
                    />
                    <CompanyCriteria
                      heading={companyCriteriaApprovedHeading}
                      criteriaList={companyApprovalCriteriaList}
                    />
                    <CompanyFieldset
                      id="contact"
                      heading={companyContactHeading}
                    >
                      <CompanyName {...companyNameField} />
                      <CompanyPhone {...companyPhoneField} />
                      <CompanyEmail {...companyEmailField} />
                    </CompanyFieldset>
                    <CompanyFieldset
                      id="services"
                      heading={companyServicesHeading}
                    >
                      {services.length > 0 ? (
                        <TypeOfServices
                          services={services}
                          {...typeOfServiceField}
                        />
                      ) : null}
                      {companyServicesLeadText && (
                        <div className="texts__lead_text">
                          <HtmlParser html={companyServicesLeadText} />
                        </div>
                      )}
                      {localesEmployers.map((locale) => (
                        <LocalDescription
                          key={locale}
                          companyDescriptionField={companyDescriptionField}
                          companyWebsiteField={companyWebsiteField}
                          locale={locale}
                        />
                      ))}
                    </CompanyFieldset>

                    <CompanyLogo
                      heading={companyLogoHeading}
                      unsupportedFileTypeErrorMessage={
                        unsupportedFileTypeErrorMessage
                      }
                      {...companyLogoField}
                    />
                    <CompanyTerms terms={companyTermsField} terms2={companyTermsField2} />

                    <CompanyFormActions />

                    <ErrorMessage {...props} />
                  </>
                ) : null}
              </Block.Content>
            </Block>
          </StateContext.Provider>
        </DispatchContext.Provider>
      </CompanyFormContext.Provider>
    </div>
  );
}
